import React from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { saveAs } from 'file-saver';

import { sendRequest, isBeyondUnlocked, triggerEvent, eventOn, eventOff } from '../helpers/global.js';
import Storage from '../helpers/Storage.js';

import RedemptionsView from './charts/RedemptionsView';
import ConversionView from './charts/ConversionView';
import DistributionView from './charts/DistributionView';
import RedemptionByHourView from './charts/RedemptionByHourView';
import TopPerformingView from './charts/TopPerformingView';
import ContactBlock from './common/ContactBlock';
import DashboardFilters from './common/DashboardFilters';
import UnlockDialog from './common/UnlockDialog';

import '../sass/components/Dashboard.scss';

const mapStoreToProps = (store) => {
  return {
    user: store.data.user,
    venues: store.data.venues || [],
    isMobile: store.setup.isMobile,
  }
};

class BeyondDashboard extends React.Component {
  constructor(props) {
    super(props);
    const filter = Storage.getData('dashboardFilter');
    this.state = {
      data: null,
      filter: {
        interval: 'weekly',
        period: 'custom',
        from: '',
        to: '',
        ...filter,
      },
      dataRequested: false,
    };
    this.contactBlock = null;
    this.sentEvents = [];

    const beyondSelected = this.props.user.venues.filter(
      i => this.props.venues.includes(i.id) && i.beyond_partner
    );
    if (beyondSelected.length > 0) {
      Storage.setData('venues', beyondSelected.map(i => i.id));
    }
  }

  getContainer = () => {
    return document.querySelector('.dashboardContainer');
  }

  componentDidMount = () => {
    triggerEvent('logAmplitude', 'redemption - view page');
    eventOn('scroll', this.onScroll, this.getContainer());
  }

  componentDidUpdate = (prevProps, prevState) => {
    if (this.props.venues !== prevProps.venues && this.state.dataRequested) {
      this.requestData();
    }
  }

  componentWillUnmount() {
    eventOff('scroll', this.onScroll, this.getContainer());
  }

  logScrollEvent = (event) => {
    if (!this.sentEvents.includes(event)) {
      this.sentEvents.push(event);
      triggerEvent('logAmplitude', event);
    }
  }

  onScroll = () => {
    if (this.contactBlock?.getBoundingClientRect()?.bottom < window.innerHeight) {
      this.logScrollEvent('redemption - ad - consult with burpple - impressions');
    }
  }

  requestData = () => {
    const beyond = isBeyondUnlocked(this.props.user, this.props.venues);
    if (!beyond) {
      return;
    }
    this.setState({dataRequested: true});
    sendRequest({
      method: 'dashboard/redemptions',
      type: 'GET',
      data: {
        ...this.state.filter,
        venue_ids: this.props.venues,
      },
      success: (data) => {
        this.setState({data});
      },
      error: (data) => {
      }
    });
  }

  onDownloadCsv = () => {
    triggerEvent('logAmplitude', 'redemption - download CSV');
    sendRequest({
      method: 'dashboard/redemptions_csv',
      type: 'GET',
      data: {
        ...this.state.filter,
        venue_ids: this.props.venues,
      },
      success: (data) => {
        const csvData = new Blob([data], { type: 'text/csv;charset=utf-8;' });
        saveAs(csvData, 'beyond.csv');
      },
      error: (data) => {
      }
    });
  }

  onDownloadRedemption = () => {
    triggerEvent('logAmplitude', 'redemption - download redemption report');
    sendRequest({
      method: 'dashboard/redemption_records_csv',
      type: 'GET',
      data: {
        ...this.state.filter,
        venue_ids: this.props.venues,
      },
      success: (data) => {
        const csvData = new Blob([data], { type: 'text/csv;charset=utf-8;' });
        saveAs(csvData, 'beyond_redemption.csv');
      },
      error: (data) => {
      }
    });
  }

  renderRedemption = (data) => {
    return (
      <div className='containterContent'>
        <div className='title'>
          Redemptions
          <div className='info'>
            <div className={classnames('tooltip', {
              'bottom': true,
              'center': this.props.isMobile,
            })}>
              Based on the number of deals redeemed
            </div>
          </div>
        </div>
        <div className='name'>Total</div>
        <div className='value'>{data.redemptions.total}</div>
        <div className='chart'>
          <RedemptionsView data={data.redemptions}/>
        </div>
      </div>
    )
  }

  renderConversion = (data) => {
    return (
      <div className='containterContent'>
        <div className='title'>
          Conversion funnel
          <div className='info'>
            <div className={classnames('tooltip', {
              'bottom': !this.props.isMobile,
              'center': this.props.isMobile,
            })}>
              Shows the flow of beyond users and the steps required to make a deal redemption
            </div>
          </div>
        </div>
        <div className='name'>Conversion rate</div>
        <div className='value'>{data.conversion_funnel.rate}%</div>
        <div className='chart'>
          <ConversionView data={data.conversion_funnel}/>
        </div>
        <div className='advise'>
          High drop-offs could be due to restrictive deal T&Cs. 
          Consider revising your deals to improve your conversion rate.
        </div>
      </div>
    )
  }

  renderDistribution = (data) => {
    return (
      <div className='containterContent'>
        <div className='title'>
          New vs returning customer
          <div className='info'>
            <div className={classnames('tooltip', {
              'left': this.props.isMobile,
            })}>
              New customers refer to beyond customers who redeemed for the first time. 
              Returning customers refer to those who redeemed for the second, third, fourth time.
            </div>
          </div>
        </div>
        <div className='chart'>
          <DistributionView data={data.new_returning_customers}/>
        </div>
        <div
          className='advise clickable'
          onClick={() => window.open(`${process.env.REACT_APP_MAIN_URL}/advertise`, '_blank')}
        >
          Looking to bring in new customers? Consider advertising with 
          burpple via social media, advertorials or ads. Find out more.
        </div>
      </div>
    )
  }

  renderRedemptionByHour = (data) => {
    return (
      <div className='containterContent'>
        <div className='title'>
          Redemption by hour
          <div className='info'>
            <div className={classnames('tooltip', {
              'center': this.props.isMobile,
            })}>
              Based on the timestamp when the deal was redeemed
            </div>
          </div>
        </div>
        <div className='chart'>
          <RedemptionByHourView data={data.redemption_by_hour}/>
        </div>
      </div>
    )
  }

  renderTopPerforming = (data) => {
    return (
      <div className='containterContent'>
        <div className='title'>
          Top performing deal
          <div className='info'>
            <div className={classnames('tooltip', {
              'center': this.props.isMobile,
            })}>
              Based on the number of redemptions, broken down by the type of deals 
              listed on burpple beyond
            </div>
          </div>
        </div>
        <div className='chart'>
          <TopPerformingView data={data.top_performing_deal}/>
        </div>
      </div>
    )
  }

  renderContactBlock = () => {
    return (
      <div className='containterContent'>
        <ContactBlock
          onContact={() => triggerEvent('logAmplitude', 'redemption - ad - consult with burpple - clicked')}
          elemRef={elem => this.contactBlock = elem}
        />
      </div>
    )
  }

  renderLocked = () => {
    return (
      <div className='dashboard locked'>
        <div className='lockedPlaceholers'>
          <img className='graphPlaceholder big' src='/images/graph_placeholder_1.png' alt=''/>
          <img className='graphPlaceholder small' src='/images/graph_placeholder_2.png' alt=''/>
          <img className='graphPlaceholder small' src='/images/graph_placeholder_3.png' alt=''/>
        </div>
        <div className='lockedOverlay'/>
        <UnlockDialog/>
      </div>
    )
  }

  render = () => {
    const beyond = isBeyondUnlocked(this.props.user, this.props.venues);
    if (!beyond) {
      return this.renderLocked();
    }

    const data = this.state.data;
    return (
      <div className='dashboard'>
        <div className='dashboardInfo'>
          <div className='dashboardHeader'>
            <div className='dashboardTitle'>Redemption Insights</div>
            <div className='dashboardSubtitle'>See how your restaurant is performing on Burpple</div>
          </div>
          <div className='dashboardControls'>
            <DashboardFilters
              withInterval
              value={this.state.filter}
              onOpen={key => {
                if (key === 'interval') {
                  triggerEvent('logAmplitude', 'redemption - view monthly dropdown');
                } else if (key === 'period') {
                  triggerEvent('logAmplitude', 'redemption - view date filter');
                }
              }}
              onChange={change => {
                if (change.interval) {
                  triggerEvent('logAmplitude', 'redemption  - use monthly dropdown');
                } else if (change.period) {
                  triggerEvent('logAmplitude', 'redemption - use date filter');
                }
                const filter = { ...this.state.filter, ...change };
                Storage.setData('dashboardFilter', {
                  ...Storage.getData('dashboardFilter'),
                  ...filter,
                });
                this.setState({filter}, this.requestData);
              }}
              onDownloadRedemption={this.onDownloadRedemption}
              onDownloadCsv={this.onDownloadCsv}
            />
          </div>
        </div>
        <div className='dashboardContainer'>
          {data ? 
            <div className='dashboardContent'>
              <div className='dashboardBlock huge'>{this.renderRedemption(data)}</div>
              <div className='dashboardBlock medium'>{this.renderConversion(data)}</div>
              <div className='dashboardBlock medium'>{this.renderDistribution(data)}</div>
              <div className='dashboardBlock huge'>{this.renderRedemptionByHour(data)}</div>
              <div className='dashboardBlock medium'>{this.renderTopPerforming(data)}</div>
              <div className='dashboardBlock medium'>{this.renderContactBlock()}</div>
            </div>
          : null}
        </div>
      </div>
    )
  }
}

export default connect(mapStoreToProps)(BeyondDashboard);
